import React from 'react';
import { Card, Badge } from 'react-bootstrap';
import { Calendar, User, Home, Clock, DollarSign } from 'lucide-react';
import { motion } from 'framer-motion';
import { Reservation } from '../../services/types';
import { formatDate, calculateNights, getStatusColor, getStatusBadgeVariant } from '../../utils/reservationHelpers';

interface ReservationCardProps {
    reservation: Reservation;
    houseName?: string;
    onClick?: () => void;
    compact?: boolean;
}

const ReservationCard: React.FC<ReservationCardProps> = ({
    reservation,
    houseName,
    onClick,
    compact = false,
}) => {
    const statusColors = getStatusColor(reservation.reservation_state);
    const nights = calculateNights(reservation.check_in_date, reservation.check_out_date);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            whileHover={{ scale: 1.02, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
            transition={{ duration: 0.2 }}
        >
            <Card
                className="border-0 shadow-sm h-100"
                style={{
                    borderLeft: `4px solid ${statusColors.border}`,
                    cursor: onClick ? 'pointer' : 'default',
                }}
                onClick={onClick}
            >
                <Card.Body className={compact ? 'p-3' : 'p-4'}>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="flex-grow-1">
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <User size={16} className="text-muted" />
                                <h6 className="mb-0 fw-semibold">
                                    {reservation.name_first_guess || 'Guest'}
                                </h6>
                            </div>
                            {houseName && (
                                <div className="d-flex align-items-center gap-2 text-muted small mb-1">
                                    <Home size={14} />
                                    <span>{houseName}</span>
                                </div>
                            )}
                        </div>
                        <Badge bg={getStatusBadgeVariant(reservation.reservation_state)}>
                            {reservation.reservation_state}
                        </Badge>
                    </div>

                    <div className="d-flex align-items-center gap-2 text-muted small mb-2">
                        <Calendar size={14} />
                        <span>
                            {formatDate(reservation.check_in_date)} → {formatDate(reservation.check_out_date)}
                        </span>
                        <span className="badge bg-light text-dark ms-1">{nights}N</span>
                    </div>

                    {!compact && (
                        <>
                            <div className="d-flex gap-3 mt-3 pt-3 border-top">
                                <div className="d-flex align-items-center gap-1 small">
                                    <Clock size={14} className="text-muted" />
                                    <span>{reservation.rooms} room{reservation.rooms > 1 ? 's' : ''}</span>
                                </div>
                                {reservation.number_of_guests && (
                                    <div className="small text-muted">
                                        {reservation.number_of_guests} guest{reservation.number_of_guests > 1 ? 's' : ''}
                                    </div>
                                )}
                                {reservation.is_paid && (
                                    <div className="d-flex align-items-center gap-1 small text-success">
                                        <DollarSign size={14} />
                                        <span>Paid</span>
                                    </div>
                                )}
                            </div>

                            {reservation.platform && (
                                <div className="mt-2">
                                    <Badge bg="info" className="small">
                                        {reservation.platform}
                                    </Badge>
                                </div>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>
        </motion.div>
    );
};

export default ReservationCard;